import { useState, useEffect, useCallback } from 'react';

/**
 * Custom hook to detect device type and handle resize events with debounce
 * @param {number} mobileBreakpoint - The maximum width (in pixels) considered mobile
 * @param {number} debounceDelay - The delay in ms for debouncing resize events
 * @returns {boolean} True if current viewport is mobile size
 */
export const useDeviceDetection = (mobileBreakpoint = 768, debounceDelay = 250) => {
  // Initial state based on window size (if available)
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= mobileBreakpoint : false
  );

  // Create debounced resize handler using useCallback
  const handleResize = useCallback(() => {
    // Only update state if the mobile/desktop classification changed
    const newIsMobile = window.innerWidth <= mobileBreakpoint;
    if (newIsMobile !== isMobile) {
      setIsMobile(newIsMobile);
    }
  }, [mobileBreakpoint, isMobile]);

  // Set up debounced resize listener
  useEffect(() => {
    // Debounced resize handler using requestAnimationFrame for better performance
    let debounceTimeout = null;
    let rafId = null;
    
    const debouncedResize = () => {
      // Cancel any pending debounce
      if (debounceTimeout) {
        clearTimeout(debounceTimeout);
      }
      
      // Cancel any pending animation frame
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
      
      // Set new timeout
      debounceTimeout = setTimeout(() => {
        // Use requestAnimationFrame to ensure resize happens during frame update
        rafId = requestAnimationFrame(() => {
          handleResize();
          debounceTimeout = null;
          rafId = null;
        });
      }, debounceDelay);
    };

    // Add event listener
    window.addEventListener('resize', debouncedResize, { passive: true });
    
    // Force initial check
    handleResize();
    
    // Remove event listener and clear pending timeouts on cleanup
    return () => {
      window.removeEventListener('resize', debouncedResize);
      if (debounceTimeout) {
        clearTimeout(debounceTimeout);
      }
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [handleResize, debounceDelay]);

  return isMobile;
};

export default useDeviceDetection;